import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, AlertTriangle, CheckCircle, Calendar, TrendingUp, BarChart3, PieChart, MapPin } from 'lucide-react';
import { PieChart as RePieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Card from '../components/Card';
import {
  AdminDashboardHero,
  AdminKpiGrid,
  AdminQuickActions,
  AdminSectionTitle,
} from '../components/admin/AdminDashboardParts';
import { apiService } from '../services/apiService';
import { formatDate, getEquipmentStatus, getStatusColor, getStatusLabel, renderCell, renderCellDate } from '../utils/helpers';
import { EQUIPMENT_TYPE_LABELS } from '../utils/constants';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalEquipment: 0,
    operational: 0,
    maintenanceRequired: 0,
    expired: 0,
    totalClients: 0,
    upcomingMaintenances: 0,
    totalSites: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentEquipment, setRecentEquipment] = useState([]);
  const [recentExtinguishers, setRecentExtinguishers] = useState([]);
  const [sites, setSites] = useState([]);
  const [maintenanceSchedule, setMaintenanceSchedule] = useState([]);
  const [upcomingMaintenances, setUpcomingMaintenances] = useState([]);
  const [equipmentByType, setEquipmentByType] = useState([]);
  const [equipmentByStatus, setEquipmentByStatus] = useState([]);
  const [maintenanceTrend, setMaintenanceTrend] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const clientId = isAdmin ? null : user?.id;
      
      // Load statistics
      const statsData = await apiService.getStatistics(clientId);
      setStats(statsData);

      // Load sites
      const sitesData = clientId 
        ? await apiService.getSitesByClient(clientId)
        : await apiService.getSites();
      setSites(sitesData);
      
      // Update total sites count
      setStats(prev => ({ ...prev, totalSites: sitesData.length }));

      // Load maintenance schedule (current month)
      const scheduleData = await apiService.getMaintenanceSchedule(clientId);
      setMaintenanceSchedule(scheduleData);

      // Load recent equipment
      const equipmentData = await apiService.getEquipment(clientId);
      setRecentEquipment(equipmentData.slice(0, 3));

      // Load recent extinguishers (only type: extinguisher)
      const extinguishers = equipmentData.filter(eq => eq.type === 'extinguisher');
      setRecentExtinguishers(extinguishers.slice(0, 3));

      // Calculate equipment by type
      const typeCount = {};
      equipmentData.forEach(eq => {
        const type = EQUIPMENT_TYPE_LABELS[eq.type] || eq.type;
        typeCount[type] = (typeCount[type] || 0) + 1;
      });
      const typeData = Object.entries(typeCount).map(([name, value]) => ({ name, value }));
      setEquipmentByType(typeData);

      // Calculate equipment by status
      const statusCount = { Terminé: 0, 'Maintenance requise': 0, Expiré: 0 };
      equipmentData.forEach(eq => {
        const status = getEquipmentStatus(eq);
        if (status === 'operational') statusCount['Terminé']++;
        else if (status === 'expiring_soon' || status === 'maintenance_required') statusCount['Maintenance requise']++;
        else if (status === 'expired') statusCount['Expiré']++;
      });
      const statusData = Object.entries(statusCount).map(([name, value]) => ({ name, value }));
      setEquipmentByStatus(statusData);

      // Calculate upcoming maintenances from equipment with next_maintenance_date
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      // Filter equipment that needs maintenance within the next 30 days (or is overdue)
      const equipmentNeedingMaintenance = equipmentData
        .filter(eq => eq.nextMaintenanceDate)
        .map(eq => ({
          ...eq,
          maintenanceDate: new Date(eq.nextMaintenanceDate),
          isOverdue: new Date(eq.nextMaintenanceDate) < now
        }))
        .filter(eq => eq.maintenanceDate <= thirtyDaysFromNow || eq.isOverdue)
        .sort((a, b) => a.maintenanceDate - b.maintenanceDate);
      
      setUpcomingMaintenances(equipmentNeedingMaintenance.slice(0, 5));

      // Calculate maintenance trend (by month) - based on equipment next maintenance dates
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      const trendData = [];
      for (let i = 5; i >= 0; i--) {
        const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = monthNames[month.getMonth()];
        const count = equipmentData.filter(eq => {
          if (!eq.nextMaintenanceDate) return false;
          const mDate = new Date(eq.nextMaintenanceDate);
          return mDate.getMonth() === month.getMonth() && mDate.getFullYear() === month.getFullYear();
        }).length;
        trendData.push({ month: monthName, maintenances: count });
      }
      setMaintenanceTrend(trendData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];
  const STATUS_COLORS = { 'Terminé': '#10b981', 'Maintenance requise': '#f59e0b', 'Expiré': '#ef4444' };

  return (
    <div className={isAdmin ? 'max-w-[1600px] space-y-4 sm:space-y-6' : 'space-y-3 sm:space-y-4'}>
      {isAdmin ? (
        <>
          <AdminDashboardHero userName={user?.name} />
          <AdminKpiGrid stats={stats} />
        </>
      ) : (
        <>
      {/* Welcome Section — client */}
      <motion.div
        initial={{ opacity: 0.9 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="relative overflow-hidden rounded-xl bg-gradient-to-br from-red-500 via-red-600 to-rose-600 p-4 shadow-xl sm:rounded-2xl sm:p-5"
      >
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-red-700/20 blur-3xl" />
        <div className="absolute right-0 top-0 h-full w-full bg-gradient-to-l from-black/10 to-transparent" />
        
        <div className="relative flex items-center justify-between">
          <div>
            <div className="mb-1 flex items-center gap-2 sm:mb-2 sm:gap-3">
              <div>
                <h1 className="text-lg font-extrabold leading-tight text-white sm:text-xl md:text-2xl">
                  Bienvenue, {user?.name}
                </h1>
                <p className="text-xs font-semibold text-red-100 sm:text-sm">Espace client</p>
              </div>
            </div>
          </div>
          
          <div className="hidden items-center gap-3 md:flex">
            <div className="rounded-xl bg-white/10 px-4 py-2 text-right backdrop-blur-sm">
              <p className="text-xs font-semibold text-red-100">Aujourd'hui</p>
              <p className="text-sm font-extrabold text-white">
                {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid — client */}
      <div className="grid grid-cols-1 gap-2 xs:grid-cols-2 sm:gap-3 lg:grid-cols-4">
        <motion.div
          initial={{ opacity: 0.9 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="rounded-lg border border-gray-100 bg-white p-3 shadow-lg transition-all hover:border-red-200 hover:shadow-2xl sm:rounded-xl sm:p-4"
        >
          <div>
            <div className="mb-2 flex items-start justify-between sm:mb-3">
              <div className="rounded-lg bg-gradient-to-br from-red-500 to-red-600 p-2 shadow-lg shadow-red-500/30 sm:rounded-xl sm:p-2.5">
                <MapPin className="h-4 w-4 text-white sm:h-5 sm:w-5" />
              </div>
              <span className="rounded-lg bg-green-50 px-2 py-0.5 text-xs font-bold text-green-600 sm:px-2.5 sm:py-1 sm:text-sm">+0%</span>
            </div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500 sm:text-sm">Total Sites</p>
            <p className="text-2xl font-extrabold leading-none text-gray-900 sm:text-3xl md:text-4xl">{stats.totalSites}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0.9 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-green-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg shadow-green-500/30">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {stats.totalEquipment > 0 ? Math.round((stats.operational / stats.totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Terminés</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{stats.operational}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0.9 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-amber-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg sm:rounded-xl shadow-lg shadow-amber-500/30">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-amber-600 bg-amber-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {stats.totalEquipment > 0 ? Math.round((stats.maintenanceRequired / stats.totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Maintenance</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{stats.maintenanceRequired}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0.9 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="rounded-lg border border-gray-100 bg-white p-3 shadow-lg transition-all hover:border-red-200 hover:shadow-2xl sm:rounded-xl sm:p-4"
        >
          <div>
            <div className="mb-2 flex items-start justify-between sm:mb-3">
              <div className="rounded-lg bg-gradient-to-br from-red-500 to-rose-600 p-2 shadow-lg shadow-red-500/30 sm:rounded-xl sm:p-2.5">
                <AlertTriangle className="h-4 w-4 text-white sm:h-5 sm:w-5" />
              </div>
              <span className="rounded-lg bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600 sm:px-2.5 sm:py-1 sm:text-sm">
                {stats.totalEquipment > 0 ? Math.round((stats.expired / stats.totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500 sm:text-sm">Expirés</p>
            <p className="text-2xl font-extrabold leading-none text-gray-900 sm:text-3xl md:text-4xl">{stats.expired}</p>
          </div>
        </motion.div>
      </div>
        </>
      )}

      {/* Maintenance Schedule - Current Month */}
      <motion.div initial={{ opacity: 0.9 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
        <Card className={isAdmin ? 'rounded-xl bg-white p-6 shadow-md' : 'border border-gray-100 shadow-lg'}>
          {isAdmin ? (
            <AdminSectionTitle
              icon={Calendar}
              title="Maintenance du mois"
              subtitle={new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            />
          ) : (
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-2 shadow-lg shadow-blue-500/30 sm:p-3">
                  <Calendar className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-gray-900 sm:text-lg md:text-xl">
                    Maintenance du Mois
                  </h3>
                  <p className="text-xs text-gray-500 sm:text-sm">
                    {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {isAdmin && (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Client
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Site
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Nombre d&apos;équipements
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Date de prochaine vérification
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {maintenanceSchedule && maintenanceSchedule.length > 0 ? (
                  maintenanceSchedule.map((item, idx) => {
                    const isOverdue = item.nextMaintenanceDate && new Date(item.nextMaintenanceDate) < new Date();
                    return (
                    <tr 
                      key={idx} 
                      className={`transition-colors cursor-pointer ${isOverdue ? 'hover:bg-red-50 bg-red-25' : 'hover:bg-blue-50'}`}
                      onClick={() => navigate(isAdmin ? '/maintenance' : '/notifications')}
                    >
                      {isAdmin && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {renderCell(item.clientName)}
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.siteName || 'Site Principal'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">
                        {item.equipmentCount || 0}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {renderCellDate(item.nextMaintenanceDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                          isOverdue 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {isOverdue ? '⚠️ En retard' : '✓ À venir'}
                        </span>
                      </td>
                    </tr>
                  );})
                ) : (
                  <tr>
                    <td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center">
                      <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-500">Aucune maintenance prévue ce mois</p>
                      <p className="text-xs text-gray-400 mt-1">Les équipements avec maintenance prévue apparaîtront ici</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div initial={{ opacity: 0.9 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
        {isAdmin ? (
          <AdminQuickActions navigate={navigate} />
        ) : (
        <Card className="border border-gray-100 shadow-lg">
          <div className="mb-3 flex items-center justify-between sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-2 shadow-lg shadow-purple-500/30 sm:rounded-2xl sm:p-3">
                <BarChart3 className="h-5 w-5 text-white sm:h-6 sm:w-6" />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-gray-900 sm:text-lg md:text-xl">Actions Rapides</h3>
                <p className="hidden text-xs text-gray-500 sm:block">Accès direct aux fonctions principales</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3 md:gap-4">
                <motion.button 
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/my-sites')}
                  className="relative overflow-hidden p-4 sm:p-5 md:p-6 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white rounded-xl sm:rounded-2xl hover:border-blue-400 hover:shadow-2xl hover:shadow-blue-500/20 transition-all text-center group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <MapPin className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 mx-auto mb-2 sm:mb-3 text-blue-600 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                    <p className="font-extrabold text-gray-900 text-sm sm:text-base mb-0.5 sm:mb-1">Mes Sites</p>
                    <p className="text-xs text-gray-500">Voir mes sites</p>
                  </div>
                </motion.button>

                <motion.button 
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/my-plan')}
                  className="relative overflow-hidden p-4 sm:p-5 md:p-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white rounded-xl sm:rounded-2xl hover:border-purple-400 hover:shadow-2xl hover:shadow-purple-500/20 transition-all text-center group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <MapPin className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 mx-auto mb-2 sm:mb-3 text-purple-600 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                    <p className="font-extrabold text-gray-900 text-sm sm:text-base mb-0.5 sm:mb-1">Mon Plan</p>
                    <p className="text-xs text-gray-500">Plan interactif</p>
                  </div>
                </motion.button>

                <motion.button 
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/my-reports')}
                  className="relative overflow-hidden p-4 sm:p-5 md:p-6 border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white rounded-xl sm:rounded-2xl hover:border-amber-400 hover:shadow-2xl hover:shadow-amber-500/20 transition-all text-center group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <AlertTriangle className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 mx-auto mb-2 sm:mb-3 text-amber-600 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                    <p className="font-extrabold text-gray-900 text-sm sm:text-base mb-0.5 sm:mb-1">Rapports</p>
                    <p className="text-xs text-gray-500">Télécharger</p>
                  </div>
                </motion.button>
          </div>
        </Card>
        )}
      </motion.div>

      {/* Analytics & Charts Section */}
      <motion.div
        initial={{ opacity: 0.9 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="space-y-3 sm:space-y-4 md:space-y-6"
      >
        {/* Main Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
          {/* Equipment Status Distribution - Enhanced */}
          <motion.div
            initial={{ opacity: 0.9 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            whileHover={{ y: -4 }}
          >
            <Card className="border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4 sm:mb-5 md:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-2 sm:p-2.5 md:p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl sm:rounded-2xl shadow-lg shadow-emerald-500/30">
                    <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-extrabold text-gray-900">État des Équipements</h3>
                    <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Distribution par statut</p>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <RePieChart>
                  <defs>
                    <filter id="shadow" height="200%">
                      <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                      <feOffset dx="0" dy="2" result="offsetblur"/>
                      <feComponentTransfer>
                        <feFuncA type="linear" slope="0.3"/>
                      </feComponentTransfer>
                      <feMerge>
                        <feMergeNode/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  <Pie
                    data={equipmentByStatus}
                    cx="50%"
                    cy="45%"
                    labelLine={false}
                    outerRadius={85}
                    innerRadius={50}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={4}
                    style={{ filter: 'url(#shadow)' }}
                  >
                    {equipmentByStatus.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={STATUS_COLORS[entry.name]}
                        stroke="#fff"
                        strokeWidth={3}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '16px', 
                      border: 'none',
                      fontSize: '13px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                      fontWeight: '600',
                      padding: '12px 16px',
                      backgroundColor: 'rgba(255,255,255,0.98)'
                    }} 
                    formatter={(value, name) => [
                      <span className="font-bold">{value} équipement{value > 1 ? 's' : ''}</span>, 
                      name
                    ]}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={50}
                    iconType="circle"
                    iconSize={10}
                    wrapperStyle={{ 
                      fontSize: '13px', 
                      fontWeight: '700',
                      paddingTop: '20px'
                    }}
                    formatter={(value, entry) => {
                      const total = equipmentByStatus.reduce((a, b) => a + b.value, 0);
                      const percent = total > 0 ? ((entry.payload.value / total) * 100).toFixed(0) : 0;
                      return <span className="text-gray-700">{value} <span className="text-gray-500 font-semibold">({percent}%)</span></span>;
                    }}
                  />
                </RePieChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>

          {/* Maintenance Trend - Enhanced Area Chart */}
          <motion.div
            initial={{ opacity: 0.9 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            whileHover={{ y: -4 }}
          >
            <Card className="border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4 sm:mb-5 md:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-2 sm:p-2.5 md:p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl shadow-lg shadow-blue-500/30">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-extrabold text-gray-900">Tendance Maintenances</h3>
                    <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Évolution sur 6 mois</p>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={maintenanceTrend} margin={{ top: 10, right: 20, left: -10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorMaint" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.5}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="colorStroke" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#3b82f6"/>
                      <stop offset="100%" stopColor="#6366f1"/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12, fill: '#6b7280', fontWeight: '600' }} 
                    stroke="#d1d5db"
                    axisLine={{ stroke: '#d1d5db' }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#6b7280', fontWeight: '600' }} 
                    stroke="#d1d5db"
                    axisLine={{ stroke: '#d1d5db' }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: '16px', 
                      border: 'none',
                      fontSize: '13px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                      fontWeight: '600',
                      padding: '12px 16px',
                      backgroundColor: 'rgba(255,255,255,0.98)'
                    }}
                    labelStyle={{ fontWeight: '700', color: '#111827' }}
                    formatter={(value) => [
                      <span className="text-blue-600 font-bold">{value} maintenance{value > 1 ? 's' : ''}</span>
                    ]}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="maintenances" 
                    stroke="url(#colorStroke)" 
                    strokeWidth={3.5} 
                    fillOpacity={1} 
                    fill="url(#colorMaint)"
                    dot={{ fill: '#3b82f6', strokeWidth: 3, r: 5, stroke: '#fff' }}
                    activeDot={{ r: 7, strokeWidth: 3, stroke: '#fff' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </div>

        {/* Equipment by Type - Bar Chart */}
        <motion.div
          initial={{ opacity: 0.9 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          whileHover={{ y: -4 }}
        >
          <Card className="border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4 sm:mb-5 md:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-2.5 md:p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl shadow-lg shadow-purple-500/30">
                  <PieChart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-extrabold text-gray-900">Équipements par Type</h3>
                  <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Distribution par catégorie</p>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={equipmentByType} margin={{ top: 10, right: 20, left: -10, bottom: 5 }}>
                <defs>
                  {equipmentByType.map((entry, index) => (
                    <linearGradient key={`gradient-${index}`} id={`colorBar-${index}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS[index % COLORS.length]} stopOpacity={1}/>
                      <stop offset="100%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0.7}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: '#6b7280', fontWeight: '600' }} 
                  stroke="#d1d5db"
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280', fontWeight: '600' }} 
                  stroke="#d1d5db"
                  axisLine={{ stroke: '#d1d5db' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip 
                  cursor={{ fill: 'rgba(0,0,0,0.05)', radius: 8 }}
                  contentStyle={{ 
                    borderRadius: '16px', 
                    border: 'none',
                    fontSize: '13px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    fontWeight: '600',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255,255,255,0.98)'
                  }}
                  labelStyle={{ fontWeight: '700', color: '#111827' }}
                  formatter={(value) => [
                    <span className="font-bold">{value} équipement{value > 1 ? 's' : ''}</span>
                  ]}
                />
                <Bar 
                  dataKey="value" 
                  radius={[12, 12, 0, 0]}
                  maxBarSize={80}
                >
                  {equipmentByType.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`url(#colorBar-${index})`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </motion.div>
      </motion.div>

      {/* Recent Equipment and Extincteurs Récents and Upcoming Maintenances */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3">
        {/* Recent Equipment */}
        <Card>
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-lg sm:rounded-xl">
              <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
            </div>
            <h3 className="text-sm sm:text-base font-extrabold text-gray-900">Équipements Récents</h3>
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            {recentEquipment.length === 0 ? (
              <div className="text-center py-4 sm:py-6">
                <Package className="w-6 h-6 sm:w-8 sm:h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-500 font-medium">Aucun équipement</p>
              </div>
            ) : (
              recentEquipment.map((equipment, index) => (
                <motion.div
                  key={equipment.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.05 }}
                  whileHover={{ x: 2 }}
                  onClick={() => navigate(isAdmin ? `/equipment/edit/${equipment.id}` : `/my-equipment/${equipment.id}`)}
                  className="flex items-center justify-between p-2.5 sm:p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg sm:rounded-xl hover:shadow-md transition-all duration-200 border border-gray-100 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm sm:text-base truncate">{equipment.siteName || 'Site Principal'}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{equipment.serialNumber}</p>
                  </div>
                  <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-bold shadow-sm ml-2 flex-shrink-0 ${getStatusColor(getEquipmentStatus(equipment))}`}>
                    {getStatusLabel(getEquipmentStatus(equipment))}
                  </span>
                </motion.div>
              ))
            )}
          </div>
        </Card>

        {/* Recent Extinguishers */}
        <Card>
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg sm:rounded-xl">
              <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
            </div>
            <h3 className="text-sm sm:text-base font-extrabold text-gray-900">Extincteurs Récents</h3>
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            {recentExtinguishers.length === 0 ? (
              <div className="text-center py-4 sm:py-6">
                <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-500 font-medium">Aucun extincteur</p>
              </div>
            ) : (
              recentExtinguishers.map((extinguisher, index) => (
                <motion.div
                  key={extinguisher.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.75 + index * 0.05 }}
                  whileHover={{ x: 2 }}
                  onClick={() => navigate(isAdmin ? `/equipment/edit/${extinguisher.id}` : `/my-equipment/${extinguisher.id}`)}
                  className="flex items-center justify-between p-2.5 sm:p-3 bg-gradient-to-r from-orange-50 to-white rounded-lg sm:rounded-xl hover:shadow-md transition-all duration-200 border border-orange-100 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm sm:text-base truncate">{extinguisher.siteName || 'Site Principal'}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{extinguisher.serialNumber}</p>
                  </div>
                  <span className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-bold shadow-sm ml-2 flex-shrink-0 ${getStatusColor(getEquipmentStatus(extinguisher))}`}>
                    {getStatusLabel(getEquipmentStatus(extinguisher))}
                  </span>
                </motion.div>
              ))
            )}
          </div>
        </Card>

        {/* Upcoming Maintenances */}
        <Card>
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl">
              <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
            </div>
            <h3 className="text-sm sm:text-base font-extrabold text-gray-900">Maintenances Prévues</h3>
          </div>
          <div className="space-y-1.5 sm:space-y-2">
            {upcomingMaintenances.length === 0 ? (
              <div className="text-center py-4 sm:py-6">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-500 font-medium">Aucune maintenance prévue</p>
              </div>
            ) : (
              upcomingMaintenances.map((equipment, index) => (
                <motion.div
                  key={equipment.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.05 }}
                  whileHover={{ x: 2 }}
                  onClick={() => navigate(isAdmin ? `/equipment/edit/${equipment.id}` : `/my-equipment/${equipment.id}`)}
                  className={`flex items-center justify-between p-2.5 sm:p-3 rounded-lg sm:rounded-xl hover:shadow-md transition-all duration-200 cursor-pointer ${
                    equipment.isOverdue 
                      ? 'bg-gradient-to-r from-red-50 to-white border border-red-200' 
                      : 'bg-gradient-to-r from-blue-50 to-white border border-blue-100'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm sm:text-base truncate">{equipment.siteName || 'Site Principal'}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{equipment.serialNumber}</p>
                  </div>
                  <div className="flex flex-col items-end ml-2 flex-shrink-0">
                    <span className={`text-xs sm:text-sm font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg ${
                      equipment.isOverdue 
                        ? 'text-red-600 bg-red-100' 
                        : 'text-blue-600 bg-blue-100'
                    }`}>
                      {equipment.isOverdue ? 'En retard' : 'À venir'}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">{formatDate(equipment.nextMaintenanceDate)}</span>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </Card>
      </div>

    </div>
  );
};

export default Dashboard;

